import React from "react";
import { useTranslations } from "next-intl";
import Head from "next/head";
import { WEBPAGE_TITLE } from "@utils/Constants";
import Page from "@components/Layout/page";
import { Leaf } from "@components/Leaf/Leaf";
import { useInView } from "@hooks/useInView";
import styles from "./AdgerdirPage.module.scss";

const STANDARDS = [
    { src: "/img/graen_skref_logo.png", alt: "Græn skref" },
    { src: "/img/un_global_logo.png", alt: "UN Global Compact" },
    { src: "/img/sasb_logo.png", alt: "SASB" },
    { src: "/img/nasdaq_logo.png", alt: "Nasdaq ESG Guide" },
];

export default function AdgerdirPage() {
    const t = useTranslations("AdgerdirPage");
    const [heroRef, heroInView] = useInView();
    const [standardsRef, standardsInView] = useInView();
    const [showcase1Ref, showcase1InView] = useInView();
    const [showcase2Ref, showcase2InView] = useInView();
    const [greenStepsRef, greenStepsInView] = useInView();
    const [ctaRef, ctaInView] = useInView();

    return (
        <Page fluid bodyClassName={styles.pageBody}>
            <Head>
                <title key="site_title">{"Aðgerðir - " + WEBPAGE_TITLE}</title>
                <meta
                    name="keywords"
                    content="Aðgerðir, ESG, sjálfbærni, græn skref"
                />
                <meta
                    property="og:title"
                    content={`${WEBPAGE_TITLE} - Aðgerðir`}
                    key="og:title"
                />
                <meta
                    name="og:description"
                    content={t("hero_desc")}
                    key="og:description"
                />
                <meta name="description" content={t("hero_desc")} />
            </Head>

            <div className={styles.wrapper}>
                <Leaf className={styles.leafBg1} plain />
                <Leaf className={styles.leafBg2} plain />
                <Leaf className={styles.leafBg3} plain />

                <section
                    ref={heroRef}
                    className={`${styles.hero} ${styles.revealTarget} ${heroInView ? styles.revealed : ""}`}
                >
                    <div className={styles.heroContent}>
                        <span className={styles.badge}>{t("badge")}</span>
                        <h1 className={styles.heroTitle}>{t("hero_title")}</h1>
                        <p className={styles.heroSubtitle}>
                            {t("hero_subtitle")}
                        </p>
                        <p className={styles.heroDesc}>{t("hero_desc")}</p>
                        <a className={styles.ctaBtn} href="/hafa-samband">
                            {t("contact_us")}
                        </a>
                    </div>
                </section>

                <section
                    ref={standardsRef}
                    className={`${styles.standards} ${styles.revealTarget} ${standardsInView ? styles.revealed : ""}`}
                >
                    <h2 className={styles.standardsTitle}>
                        {t("standards_title")}
                    </h2>
                    <p className={styles.standardsDesc}>
                        {t("standards_desc")}
                    </p>
                    <div className={styles.logosRow}>
                        {STANDARDS.map((s) => (
                            <div key={s.alt} className={styles.logoCard}>
                                <img
                                    className={styles.logoImg}
                                    src={s.src}
                                    alt={s.alt}
                                />
                            </div>
                        ))}
                    </div>
                </section>

                <div className={styles.showcase}>
                    <section
                        ref={showcase1Ref}
                        className={`${styles.showcaseRow} ${styles.revealTarget} ${showcase1InView ? styles.revealed : ""}`}
                    >
                        <div className={styles.showcaseText}>
                            <span className={styles.showcaseLabel}>
                                {t("showcase_actions_label")}
                            </span>
                            <h2 className={styles.showcaseTitle}>
                                {t("showcase_actions_title")}
                            </h2>
                            <p className={styles.showcaseDesc}>
                                {t("showcase_actions_desc")}
                            </p>
                        </div>
                        <div className={styles.showcaseVisual}>
                            <div className={styles.statGrid}>
                                <div className={styles.statCard}>
                                    <span className={styles.statNumber}>
                                        120+
                                    </span>
                                    <span className={styles.statLabel}>
                                        {t("stat_actions")}
                                    </span>
                                </div>
                                <div className={styles.statCard}>
                                    <span className={styles.statNumber}>3</span>
                                    <span className={styles.statLabel}>
                                        {t("stat_categories")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("stat_categories_esg")}
                                    </span>
                                </div>
                                <div
                                    className={`${styles.statCard} ${styles.statCardWide}`}
                                >
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <circle cx="12" cy="12" r="10" />
                                            <circle cx="12" cy="12" r="6" />
                                            <circle cx="12" cy="12" r="2" />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("stat_systematic")}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section
                        ref={showcase2Ref}
                        className={`${styles.showcaseRow} ${styles.reversed} ${styles.revealTarget} ${showcase2InView ? styles.revealed : ""}`}
                    >
                        <div className={styles.showcaseText}>
                            <span className={styles.showcaseLabel}>
                                {t("showcase_structure_label")}
                            </span>
                            <h2 className={styles.showcaseTitle}>
                                {t("showcase_structure_title")}
                            </h2>
                            <p className={styles.showcaseDesc}>
                                {t("showcase_structure_desc")}
                            </p>
                        </div>
                        <div className={styles.showcaseVisual}>
                            <div className={styles.categoryGrid}>
                                <div className={styles.categoryCard}>
                                    <div className={styles.categoryIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                                            <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
                                            <path d="M2 12h20" />
                                        </svg>
                                    </div>
                                    <span className={styles.categoryName}>
                                        Umhverfislegir þættir
                                    </span>
                                </div>
                                <div className={styles.categoryCard}>
                                    <div className={styles.categoryIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                            <circle cx="9" cy="7" r="4" />
                                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                        </svg>
                                    </div>
                                    <span className={styles.categoryName}>
                                        Félagslegir þættir
                                    </span>
                                </div>
                                <div className={styles.categoryCard}>
                                    <div className={styles.categoryIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                            <polyline points="22 4 12 14.01 9 11.01" />
                                        </svg>
                                    </div>
                                    <span className={styles.categoryName}>
                                        Stjórnarþættir
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                <section
                    ref={greenStepsRef}
                    className={`${styles.greenSteps} ${styles.revealTarget} ${greenStepsInView ? styles.revealed : ""}`}
                >
                    <div className={styles.greenStepsInner}>
                        <span className={styles.showcaseLabel}>
                            {t("green_steps_label")}
                        </span>
                        <h2 className={styles.greenStepsTitle}>
                            {t("green_steps_title")}
                        </h2>
                        <p className={styles.greenStepsDesc}>
                            {t("green_steps_desc")}
                        </p>
                        <p className={styles.greenStepsDesc}>
                            {t("impact_desc")}
                        </p>
                    </div>
                </section>

                <section
                    ref={ctaRef}
                    className={`${styles.bottomCta} ${styles.revealTarget} ${ctaInView ? styles.revealed : ""}`}
                >
                    <div className={styles.bottomCtaContent}>
                        <h2 className={styles.bottomCtaTitle}>
                            {t("bottom_title")}
                        </h2>
                        <p className={styles.bottomCtaDesc}>
                            {t("bottom_desc")}
                        </p>
                        <a className={styles.ctaBtnLight} href="/hafa-samband">
                            {t("contact_us")}
                        </a>
                    </div>
                </section>
            </div>
        </Page>
    );
}
